import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    InputAdornment,
    TextField,
    Typography
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { TokenField } from '../htmlTokenSchema';

// The token picker: a compact dialog with a search field and the catalog
// fields grouped by category as a button grid (replaces the long single-
// column menu). Clicking a field inserts it at the block's caret.
const TokenPickerDialog = ({ open, fields, onPick, onClose }: {
    open: boolean,
    fields: TokenField[],
    onPick: (field: TokenField) => void,
    onClose: () => void,
}): React.ReactElement => {
    const [query, setQuery] = useState('');
    // The catalog fields matching the search, grouped by category.
    const groups = useMemo(() => {
        const lower = query.toLowerCase();
        const map: Record<string, TokenField[]> = {};
        fields.forEach((f) => {
            if (f.label.toLowerCase().includes(lower) || f.path.toLowerCase().includes(lower)) {
                const cat = f.category || 'General';
                (map[cat] ??= []).push(f);
            }
        });
        return map;
    }, [query, fields]);
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm">
            <DialogTitle>Insert token</DialogTitle>
            <TextField
                size="small"
                variant="outlined"
                placeholder="Search fields..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search fontSize="small" color="disabled" />
                        </InputAdornment>
                    )
                }}
            />
            <Box sx={{ maxHeight: 420, overflowY: 'auto', pb: 1 }}>
                {Object.keys(groups).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                        No fields match your search.
                    </Typography>
                ) : (
                    Object.entries(groups).map(([category, items]) => (
                        <Box key={category} sx={{ mb: 1.5 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.5px' }}>
                                {category} ({items.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {items.map((f) => (
                                    <Button key={f.path} size="small" variant="outlined" onClick={() => onPick(f)}>
                                        {f.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    ))
                )}
            </Box>
        </Dialog>
    );
};

export default TokenPickerDialog;
